/**
 * 工具方法
 */
import moment from 'moment/dist/moment';
import Conf from '@src/common/conf';
import revision from '@src/common/revision';
import xss from 'xss';

const Core = {
  env: import.meta.env.MODE,
  IS_LOCAL: location.host.includes("local"),
  IS_PROD: (["production", "online", "master"].includes(import.meta.env.MODE)) && localStorage.getItem('helper_env') != "test",
  IS_ONLINE: ["online", "master", "devonline"].includes(import.meta.env.MODE),
  revision,

  xss: (() => {
    const zmxss = new xss.FilterXSS({
      // @ 飞机票等
      onIgnoreTag: (tag, html, options) => {
        if (/^@/.test(tag)) {
          return html;
        }
      },
      // 需要保留的 attr
      onIgnoreTagAttr: (tag, name, value, isWhiteAttr) => {
        if (/^style|class|data\-/i.test(name)) {
          return `${name}='${value}'`;
        }
      },
    });
    return (...props) => {
      return zmxss.process(...props);
    };
  })(),

  /**
   * 获取 url 参数
   * @param {?String} href
   */
  getParams(href) {
    href = (href || window.location.href).replace(/#.*$/, '');
    let index = href.indexOf('?'),
      params,
      parts;

    if (index == -1) {
      return {};
    } else {
      parts = href.substring(index + 1).split('&');
      params = {};
    }

    parts.map((kv) => {
      let temp = kv.split('=');
      if (typeof temp[1] == 'undefined') {
        return;
      }
      if (temp[0]) {
        let v = temp[1];
        try {
          v = decodeURIComponent(v);
        } catch (e) { }
        params[temp[0]] = v;
      }
    });

    return params;
  },
  getQueryString(name){
    var reg = new RegExp("(^|&)"+ name +"=([^&]*)(&|$)");
    var r = window.location.search.substr(1).match(reg);
    if(r!=null)return  unescape(r[2]); return null;
  },
  /**
   * 获取无参数的 url
   * @param {String} href
   * @return {String}
   */
  getUrlWithOutParams(href) {
    href = href || location.href;
    return href.replace(/\?.*$/, '').replace(/\#.*$/, '');
  },

  /**
   * 输出 html
   * @param {String} str
   */
  getFormatHtml(str) {
    if (!str || typeof str != "string") {
      return '';
    }
    return str.replace(/\n/g, "<br>").replace(/\s/g, "&nbsp;");
  },

  /**
   * 去除 html 标签
   * @param {String} text
   */
  getHtmlText(text) {
    try {
      return text.toString().replace(/<[A-Za-z\/]+[^>]+>/g, '');
    } catch (e) {}
    return '';
  },

  /**
   * 获取唯一ID
   * @return {String}     唯一ID
   * @example
   *     uniqueId();
   *     // "_1383209474505_1"
   */
  uniqueId: (() => {
    let time = `_${+ new Date}_`,
      index = 0;

    return function () {
      return time + ++index;
    };
  })(),

  /**
   * 字符串截断
   * @param  {String}     str     要截断的字符串
   * @param  {Number}     len     要截取的长度，截取的长度，汉字为2，英文为1  不算tail的长度，更灵活些
   * @param  {String}     tail    截取后添加的尾部
   * @return {String}     截取的字符串
   */
  substring(str, len, tail) {
    if (!str) {
      return '';
    }
    tail = tail || '';
    let strlen = str.replace(/([^\x00-\xff])/g, '$1 ').length;
    return strlen > len
      ? str
        .substr(0, len)
        .replace(/([^\x00-\xff])/g, '$1 ')
        .substr(0, len)
        .replace(/[^\x00-\xff]$/, '')
        .replace(/([^\x00-\xff]) /g, '$1') + tail
      : str;
  },
  subfilename(str, len, tail) {
    if (!str) {
      return '';
    }
    // 无效文件名
    if (str.indexOf(".") == -1) {
      return this.substring(str, len, tail);
    }
    if (this.lenstring(str) <= len) {
      return str;
    }
    const pointIndex = str.lastIndexOf(".");
    const fileName = str.slice(0, pointIndex);
    const fileTail = str.slice(pointIndex);
    return `${this.substring(fileName, len - this.lenstring(fileTail), tail)}${this.getFileNameExt(str)}`;
  },
  lenstring(str) {
    let strlen = str.replace(/([^\x00-\xff])/g, '$1 ').length;
    return strlen;
  },

  /**
   * 过滤掉文本中不能显示的内容
   * 1. 不能显示的 emoji
   */
  getSafeText(text) {
    return text;

    if (!text || typeof text != "string") {
      return text;
    }
    // 不能显示的 emoji
    text = text.replace(/\ud83e[\udc00-\udfff]/g, "");
    return text;
  },

  /**
   * 服务端数据预处理
   */
  downloadLevels: [
    {
      key: '100000000',
      value: '亿',
    },
    {
      key: '10000',
      value: '万',
    },
  ],
  downloadLevels2: [
    {
      key: '100000000',
      value: '亿',
    },
    {
      key: '10000',
      value: 'W',
    },
  ],
  sizeLevels: [
    {
      key: '1048576',
      value: 'M',
    },
    {
      key: '1024',
      value: 'K',
    },
  ],
  /**
   * 下载量，包大小 格式化
   * @param {Number} number 数值
   * @param {Array}  levelArray 单位进制数组
   * @param {String} unit 额外的单位
   * @param {Number} fixed 保留小数位数
   * @return {String} 格式化后的值
   * @example
   *     data['download_times'] = getFixedText(data['download_times'], downloadLevels, '人', 2);
   *     // 35万人
   *     data['apk_sizes'] = getFixedText(data['apk_sizes'], sizeLevels, 'B', 2);
   *     // 3.43MB
   */
  getFixedText(number, levelArray, unit, fixed) {
    if (!number) {
      return 0 + unit;
    }
    for (let i = 0, len = levelArray.length; i < len; i++) {
      let data = levelArray[i];
      let key = data.key;
      let value = data.value;
      if (number / key >= 1) {
        // 只保留整数
        if (fixed == 0) {
          return Math.floor(number / key) + value + unit;
        }
        // 保留所有小数位
        else if (fixed == 100) {
          return number / key + value + unit;
        }
        // 保留指定小数位
        return (number / key).toFixed(fixed) + value + unit;
      }
    }
    return number + unit;
  },
  /**
   * 包大小 格式化
   * @param {Number} number 数值
   * @param {Number} fixed 保留小数位数
   * @return {String} 格式化后的值
   * @example
   *     data['apk_sizes'] = getFixedTextSize(data['apk_sizes'], 2);
   *     // 3.43MB
   */
  getFixedTextSize(number, fixed = 0) {
    return this.getFixedText(number, this.sizeLevels, 'B', fixed);
  },
  /**
   * 下载量 格式化
   * @param {Number} number 数值
   * @param {String} unit 额外的单位
   * @param {Number} fixed 保留小数位数
   * @return {String} 格式化后的值
   * @example
   *     data['download_times'] = getFixedTextTime(data['download_times'], '人', 2);
   *     // 35万人
   */
  getFixedTextTime(number, unit = '', fixed = 0) {
    return this.getFixedText(number, this.downloadLevels, unit, fixed);
  },
  /**
   * 下载量 格式化
   * @param {Number} number 数值
   * @param {String} unit 额外的单位
   * @param {Number} fixed 保留小数位数
   * @return {String} 格式化后的值
   * @example
   *     data['download_times'] = getFixedTextTime2(data['download_times'], '人', 2);
   *     // 35万人
   */
   getFixedTextTime2(number, unit = '', fixed = 0) {
    return this.getFixedText(number, this.downloadLevels2, unit, fixed);
  },

  /**
   * 最大 99
   * @param {Number}} number
   */
  getFixedNumberMax(number, max = 99) {
    if (number > max) {
      return `${max}+`;
    }
    return String(number);
  },

  /**
   * 获取图标地址
   * @param {String} name
   * @param {?String} ext
   * @example
   *    backgroundImage : `url(${getImageUrl('icon_group_60')})`
   *    backgroundImage : `url(${getImageUrl('icon_group_60', 'jpg')})`
   *    backgroundImage : `url(${getImageUrl('@assets/icons/icon_group_60.png')})`
   *    backgroundImage : `url(${getImageUrl('@assets/emojis/emoji-large/emoji_1@2x.png')})`
   */
  getImageUrl(name, ext = 'png') {
    if (/^(http|data\:image)/.test(name)) {
      return name;
    }

    let path = /^@assets/.test(name)
      ? name
      : `@assets/icon/${name}.${ext}`;

    path = path.replace(/^@assets\//, "");
    return new URL(`../../assets/${path}`, import.meta.url).href;
  },

  /**
   * 获取背景图 style
   * @param {String} name
   * @param {?String} position
   * @param {?String} size
   * @param {?String} ext
   * @param {?String} repeat
   * @example
   *    style={getBackgroundImageStyle('icon_group_60', '10px 10px')}
   *    style={getBackgroundImageStyle('icon_group_60', '10px center', '20px 20px', 'png')}
   */
  getBackgroundImageStyle(name = Conf.appIcon, position = 'center center', size = "cover", ext = "png", repeat = "no-repeat") {
    if (!name) {
      return {};
    }
    return {
      backgroundImage: `url(${this.getImageUrl(name, ext)})`,
      backgroundSize: size,
      backgroundPosition: position,
      backgroundRepeat: repeat,
    }
  },

  /**
   * 拷贝一个对象
   * @param {Object} obj
   * @param {?Array} filter_key_list    要过滤的 key
   * @return {Object}
   * @example
   *    copyObj({
   *      a : 1,
   *      b : 1,
   *    })
   *    // {a : 1, b : 1}
   *    copyObj({
   *      a : 1,
   *      b : 1,
   *    }, ["a"])
   *    // {a : 1}
   */
  copyObj(obj, filter_key_list) {
    if (typeof obj == 'undefined') {
      return undefined;
    }
    if (typeof obj != 'object') {
      return obj;
    }
    return JSON.parse(JSON.stringify(obj, filter_key_list));
  },

  /**
   * 获取图片尺寸
   * @param {String} url 
   */
  getImageSize (url) {
    return new Promise((relove, reject) => {
      let image = new Image();
      image.onload = () => {
        relove({
          width : image.naturalWidth,
          height : image.naturalHeight,
        });
      }
      image.onerror = () => {
        reject();
      }
      image.crossorigin = 'anonymous';
      image.src = url;
    });
  },

  /**
   * 参数序列化
   * @param {Object} data
   */
  param(data) {
    if (!data) {
      return '';
    }

    let keys = Object.keys(data);
    let params = [];
    keys.map((key) => {
      params.push(key + '=' + encodeURIComponent(data[key]));
    });
    return params.join('&');
  },

  /**
   * 获取地址栏参数
   * @param {String} name
   */
  getQueryString(name) {
    var reg = new RegExp("(^|&)" + name + "=([^&]*)(&|$)", "i");
    var r = window.location.search.substr(1).match(reg);
    if (r != null) return decodeURIComponent(r[2]); return null;
  },

  /**
   * 合并两个数组，去重
   * @param {Array} arr1
   * @param {Array} arr2
   */
  mergeArray(arr1, arr2) {
    let subArr = arr2.filter(v => !arr1.includes(v));
    return arr1.concat(subArr);
  },

  /**
   * 取 arr1 - arr2
   * @param {Array} arr1
   * @param {Array} arr2
   */
  subArray(arr1, arr2) {
    return arr1.filter(v => !arr2.includes(v));
  },

  /**
   * 字符串数组去重
   */
  removeRepeatArray(arr) {
    let obj = {};
    arr.forEach(key => {
      obj[key] = true;
    });
    return Object.keys(obj);
  },

  /**
  * 复杂数组去重
  */
  uniqueArray(arr, uniId) {
    if (!arr) return;
    const res = new Map();
    return arr.filter((item) => !res.has(item[uniId]) && res.set(item[uniId], 1));
  },

  /**
   * 获取视频时长的 时:分:秒 表达方式
   * @param {Number} number  数值
   * @return {String}         格式化后的值
   * @example
   *     let durFixed = Helper.getFixedTime(423);
   *     // 07:03
   *     let durFixed = Helper.getFixedTime(3623);
   *     // 01:00:23
   */
  getFixedTime(number) {
    if (!number) {
      return '';
    }
    let minute = Math.floor((number % 3600) / 60);
    let second = number % 60;

    let _time = [minute, second];
    if (number >= 3600) {
      _time.unshift(Math.floor(number / 3600));
    }
    if (_time.map) {
      return _time
        .map(function (val) {
          return ('0' + val).slice(-2);
        })
        .join(':');
    } else {
      return _time.join(':');
    }
  },

  /**
   * 获取[min, max]的一个随机数，均为整数
   * @param  {Number} min
   * @param  {Number} max
   * @return {Number} [min, max]的随机数
   */
  getRnd(min, max) {
    if (typeof max == 'undefined') {
      max = min;
      min = 0;
    }
    return min + parseInt(Math.random() * (max - min + 1));
  },

  /**
   * 从 list 中随机取 cnt 个
   * @param {Array} list
   * @param {Number} cnt
   */
  getRndFromArray(list, cnt = 1) {
    list = this.copyObj(list);
    list.sort((a, b) => Math.random() > 0.5 ? 1 : -1);
    return cnt == 1 ? list[0] : list.slice(0, cnt);
  },

  /**
   * JSON 格式化
   * @param {Object} data
   */
  getFormatedJSONString(data) {
    return JSON.stringify(data, null, '\t');
  },

  /**
   * 中间的几位打码
   * @param {String} phone
   * @param {Int} cnt 打码位数
   */
  getFormatedPhone(phone, cnt = 4) {
    if (!phone || typeof phone != "string") {
      return phone;
    }

    if (phone.length <= cnt) {
      return phone.replace(/./g, "*");
    }
    let cntPrev = Math.ceil((phone.length - cnt) / 2);
    return `${phone.slice(0, cntPrev)}${phone.slice(cntPrev, cnt).replace(/./g, "*")}${'********************'.slice(0, cnt)}${phone.slice(cntPrev + cnt)}`;
  },

  /**
    * 国际标准ISO8601 时间
    * @param {Date} date 默认当前时间
    */
  getISO8601Time(date = new Date) {
    let h = date.getHours() + 8;
    date.setHours(h);
    //2021-11-20T19:01:01+08:00
    return (new Date(date)).toISOString().replace(/\.[\d]{3}Z/, '+08:00');
  },

  /**
   * 日期格式化
   * @param  {Date} e     日期
   * @param  {String} t   格式
   * @return {String}     格式化后的字符串
   * @example
   *     formatDate(new Date(), "yyyy-MM-dd EE");
   *     // 2013-07-26 周五
   * @description qq  季度
   * @description EE  周x
   * @description EEE 星期x
   * @description hh:mm:ss
   * @description yyyy-MM-dd
   */
  formatDate: function (e, t) {
    let n = {
      'M+': e.getMonth() + 1,
      'd+': e.getDate(),
      'h+': e.getHours(),
      'H+': e.getHours(),
      'm+': e.getMinutes(),
      's+': e.getSeconds(),
      'q+': Math.floor((e.getMonth() + 3) / 3),
      S: e.getMilliseconds(),
    },
      r = {
        0: '\u65e5',
        1: '\u4e00',
        2: '\u4e8c',
        3: '\u4e09',
        4: '\u56db',
        5: '\u4e94',
        6: '\u516d',
      };
    /(y+)/.test(t) &&
      (t = t.replace(
        RegExp.$1,
        (e.getFullYear() + '').substr(4 - RegExp.$1.length),
      )),
      /(E+)/.test(t) &&
      (t = t.replace(
        RegExp.$1,
        (RegExp.$1.length > 1
          ? RegExp.$1.length > 2
            ? '\u661f\u671f'
            : '\u5468'
          : '') + r[e.getDay() + ''],
      ));
    for (var i in n)
      new RegExp('(' + i + ')').test(t) &&
        (t = t.replace(
          RegExp.$1,
          RegExp.$1.length == 1
            ? n[i]
            : ('00' + n[i]).substr(('' + n[i]).length),
        ));
    return t;
  },

    /**
   * 时间格式化
   * 时间显示规则，以推荐时间为基准时间
      当天的显示
      HH点 MM分
      昨天和之前的
      MM月 DD日 HH点 MM分
      一年前的
      YYYY年 MM月 DD日 HH点 MM分
   */
  shortTimeSpecial(date) {
    if (!date) {
      return '';
    }
    if (
      typeof date == 'number' ||
      (typeof date == 'string' && /^\d+$/.test(date))
    ) {
      date = moment(date);
    }
    const today = moment().clone().hour(0).minute(0).second(0).millisecond(0);
    const diffDay = date.diff(today, 'days', true);
    if (diffDay >= 0 && diffDay < 1) {
      return date.format('HH:mm');
    } else if (diffDay <= -365) {
      return date.format('YYYY年M月D日 HH:mm');
    } else {
      return date.format('M月D日 HH:mm');
    }
  },

  longTimeSpecial(date, frommatTxt) {
    if (!date) {
      return '';
    }
    if (frommatTxt) {
      //秒时间
      date = moment.unix(date);
      return date.format(frommatTxt);
    }
    if (
      typeof date == 'number' ||
      (typeof date == 'string' && /^\d+$/.test(date))
    ) {
      date = moment(date);
    }
    return date?.format('YYYY-MM-DD HH:mm:ss');
  },

  // 日期分组
  // content:''
  // conversation_id:''
  // cooperation:null
  // is_local:0
  // role_id:"7798404f6187547a0d90ec0d828a9581"
  // title:"生成大纲"
  // update_time: 1740102987233
  // 今天，7天，30天，年
  // let today = new Date().setHours(23, 59, 59, 999)
  // let oneDayTime = 24*60*60*1000
  // let tempArr = [
  //   {type:'group',title:'今天',update_time:today},
  //   {type:'group',title:'前7天',update_time:today-7*oneDayTime},
  //   {type:'group',title:'前30天',update_time:today-30*oneDayTime},
  //   {type:'group',title:'2025年',update_time:today-30*oneDayTime-1},// 30天往前多1ms都是2025年的数据
  //   {type:'group',title:'2024年',update_time:new Date('2024-12-31').setHours(23, 59, 59, 999)},
  // ]
  // 时间分组函数
  getTimeGroup ({ update_time, pin_top_time }) {
    if (pin_top_time) {
      return '置顶';
    }
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    const date = new Date(update_time);
    const diffDays = Math.floor((now - date) / (24 * 60 * 60 * 1000));
    if (diffDays === 0) {
      return '今天';
    } else if (diffDays <= 1) {
      return '昨天';
    } else if (diffDays <= 2) {
      return '前天';
    } else if (diffDays <= 7) {
      return '最近7天';
    } else {
      return `更早`;
    }
  },

  /**
    * 手机号码加密规则 第二位与第四位交换，第六位与第八位交换。然后把每一位数字 + 49(其实要加65，？)，再转换为字母
    * @param {Date} date 默认当前时间
  */
  mobile2letter(mobile) {
    mobile += '';
    let arr = mobile.split('');
    arr = swapArr(arr, 1, 3);
    arr = swapArr(arr, 5, 7);

    function swapArr(arr, index1, index2) {
      arr[index1] = arr.splice(index2, 1, arr[index1])[0];
      return arr;
    };

    return arr.map((item) => {
      return String.fromCharCode(item * 1 + 65);
    }).join('').toLowerCase();
  },

  changeSKin(option = {}) {
    option.W = option.W || screen.width;
    option.H = option.H || screen.height;
    option.w = option.w || 200;
    option.h = option.h || 200;
    let durationTime = 1.5;//s
    let bgColor = 'skyblue';
    let oBomp = document.createElement('div');
    oBomp.style.cssText = [
      `position:absolute;`,
      `width:100%;`,
      `height:100%;`,
      `left:0;`,
      `right:0;`,
      `top:0;`,
      `bottom:0;`,
      `z-index:9999;`,
      `overflow:hidden;`
    ].join('');
    document.body.appendChild(oBomp);

    let R = option.H / option.h;
    let C = option.W / option.w;
    let cw = option.W / 2;
    let ch = option.H / 2;
    for (let i = 0; i < R; i++) {
      for (let j = 0; j < C; j++) {
        var left = option.W * j / C;
        var top = option.H * i / R;
        var oTag = document.createElement('div');
        oTag.style.cssText = [
          `position:absolute;`,
          `width:${option.w}px;`,
          `height:${option.h}px;`,
          `left:${left}px;`,
          `top:${top}px;`,
          //`background:${bgColor};`,
          `background:url(${'http://p0.qhimg.com/t011e96105df0fc8cc0.png'}) -${left}px -${top}px no-repeat;`,
          `opacity:1;`,
          `transition:all ${durationTime}s ease;`
        ].join('');
        oBomp.append(oTag);
        let l = ((left + option.W / (2 * C)) - cw) * this.getRnd(2, 3) + cw - option.W / (2 * C);
        let t = ((top + option.H / (2 * R)) - ch) * this.getRnd(2, 3) + ch - option.H / (2 * R);
        oTag.setAttribute('l', l);
        oTag.setAttribute('t', t);
      }
    }

    setTimeout(() => {
      let aTag = oBomp.querySelectorAll('div');
      for (let i = 0; i < aTag.length; i++) {
        let oTag = aTag[i];
        let l = oTag.getAttribute('l');
        let t = oTag.getAttribute('t');

        oTag.style.cssText += [
          `opacity:0;`,
          `transform:perspective(500px) rotate(${this.getRnd(-180, 180)}deg) rotateY(${this.getRnd(-180, 180)}deg) rotateZ(${this.getRnd(-180, 180)}deg) scale(${this.getRnd(1.5, 3)});`,
          `left:${l}px;`,
          `top:${t}px;`,
        ].join('');
      }

      setTimeout(function () {
        oBomp.remove();
      }, durationTime * 1000);
    }, 500);
  },

  /**
   * 补齐前导零
   * @param {Number} num
   * @param {Number} cnt
   */
  getFixedPrev0(num, cnt = 2) {
    if (String(num).length >= cnt) return num;
    let prev = '00000000000000000000';
    return `${prev}${num}`.slice(-cnt);
  },

  /**
   * 补齐后面的.0
   * @param {Number} num
   * @param {Number} cnt
   */
  getFixedSuffix0(num) {
    return `${num}.0`;
  },
  

  /**
   * 获取图片信息
   * @param {Blob} file
   * @param {Boolean} needBase64 是否需要返回图片 base64，默认 false
   * @return {Object}
   * @example
   *    getImageInfo(inputElement.files[0])
   *    .then(info => {
   *      info : {
   *        width,
   *        height,
   *        size,
   *        base64
   *      }
   *    })
   *    读取失败时返回:
   *    info : {
   *      size,
   *    }
   */
  imageInfoCache: [], // file needBase64 info
  getImageInfo(file, needBase64 = false) {
    const cacheInfo = this.imageInfoCache.filter(item => item.file == file && item.needBase64 == needBase64);
    if (cacheInfo.length) {
      // console.log("getImageInfo cache");
      return Promise.resolve(cacheInfo[0].info);
    }
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const data = event.target.result;
        const image = new Image();
        image.onload = () => {
          const res = {
            width: image.width,
            height: image.height,
            size: file.size,
            type: file.type,
            ...(
              needBase64
                ? {
                  base64: data,
                }
                : {}
            )
          }
          this.imageInfoCache.push({
            file,
            needBase64,
            info: res,
          })
          resolve(res);
        };
        image.onerror = () => {
          resolve({
            size: file.size,
          });
        }
        image.src = data;
      };
      reader.readAsDataURL(file);
    });
  },

  /**
   * 获取视频信息
   * @param {Blob} file
   * @param {Number} thumbScale
   * @return {Object}
   * @example
   *    getVideoInfo(inputElement.files[0])
   *    .then(info => {
   *      info : {
   *        width,
   *        height,
   *        duration,
   *        size,
   *        thumb,
   *      }
   *    })
   *    读取失败时返回:
   *    info : {
   *      size,
   *    }
   */
  getVideoInfo(file, thumbScale = 0.25) {
    return new Promise((resolve, reject) => {
      let blob = URL.createObjectURL(file);
      let $video = document.createElement("video");
      $video.muted = true;

      let canvas = document.createElement("canvas");
      let ctx = canvas.getContext('2d');

      let handler = () => {
        $video.removeEventListener("canplay", handler);
        let thumb = null;

        // 不需要截图
        if (thumbScale == null) {

        }
        else {
          // 如果视频尺寸太大，限制缩放比例
          // 缩略图限制在 50k 左右，自测一张 100000 的图，大概是 50k
          if ($video.videoWidth * $video.videoHeight * Math.pow(thumbScale, 2) >= 100000) {
            thumbScale = Math.sqrt(100000 / $video.videoWidth / $video.videoHeight);

            // console.log("缩略图太大，指定为:", thumbScale);
          }

          canvas.width = parseInt($video.videoWidth * thumbScale, 10);
          canvas.height = parseInt($video.videoHeight * thumbScale, 10);

          $video.pause();
          $video.currentTime = 0;
          ctx.drawImage($video, 0, 0, canvas.width, canvas.height);
          thumb = canvas.toDataURL("image/jpeg");
        }

        // meta 头后置的视频，取不到 meta 信息(width/height/duration/thumb)
        if (!$video.videoWidth) {
          resolve({
            size: file.size,
          });
        }
        else if (thumbScale == null) {
          resolve({
            duration: $video.duration,
            width: $video.videoWidth,
            height: $video.videoHeight,
            size: file.size,
          });
        }
        else {
          resolve({
            duration: $video.duration,
            width: $video.videoWidth,
            height: $video.videoHeight,
            size: file.size,
            thumb,
          });
        }

        $video.remove();
        $video.src = null;
        URL.revokeObjectURL(blob);
      }

      $video.addEventListener("canplay", handler);
      // 不支持的视频格式
      $video.addEventListener("error", () => {
        $video.removeEventListener("canplay", handler);
        reject();
      });
      $video.src = blob;
      $video.play();
    });
  },
  /**
   * 获取图片的主色、第一色
   * @param {Blob} file
   * @param {Boolean} needMost 是否需要主色
   */
  getImageColor(file, needMost = false) {
    const beginTime = +new Date;

    return new Promise(resolve => {
      this.getImageInfo(file, true)
        .then(imageInfo => {
          const { width, height } = imageInfo;
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext('2d');
          // canvas 要尽量小，减少计算耗时
          // 50 需要主色约 1s，不需要主色约 20ms
          canvas.width = 50;
          canvas.height = canvas.width / width * height;
          let image = new Image();
          image.onload = () => {
            ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
            const { data: imageData } = ctx.getImageData(0, 0, canvas.width, canvas.height);
            if (!needMost) {
              const res = {
                first: this.rgbToHex(imageData.slice(0, 3)),
                time: (+new Date - beginTime) / 1000,
              };
              // console.log("getImageColor res:", res);
              resolve(res);
              return;
            }

            let colors = {};
            let firstColor = null;
            let mostColor = null;
            let mostCnt = 0;
            for (let i = 0, len = imageData.length; i < len; i += 4) {
              const color = `${imageData[i]}_${imageData[i + 1]}_${imageData + 2}`;
              firstColor = firstColor || color;
              colors[color] = colors[color] || 0;
              colors[color]++;
              if (colors[color] > mostCnt) {
                mostCnt = colors[color];
                mostColor = color;
              }
            }
            const res = {
              first: this.rgbToHex(firstColor.split("_")),
              most: this.rgbToHex(mostColor.split("_")),
              time: (+new Date - beginTime) / 1000,
            };
            // console.log("getImageColor res:", res);
            resolve(res);
          }
          image.src = imageInfo.base64;
        })
    })
  },
  /**
   * rgb 转 hex
   * @param {String|Array} rgb
   */
  rgbToHex(rgb) {
    if (typeof rgb == "string") {
      const reg = /(\d{1,3}),(\d{1,3}),(\d{1,3})/;
      rgb = reg.exec(rgb).slice(1, 4);
    }

    const hex = (x) => {
      return this.getFixedPrev0(parseInt(x).toString(16));
    }

    const res = `#${hex(rgb[0])}${hex(rgb[1])}${hex(rgb[2])}`
    return res;
  },

  /**
   * 获取帖子相关图片尺寸样式
   * @param {Object} media
   * @param {String} type
   */
  getPostImgSize(media, type = 'detail') {
    if (!media) {
      return {};
    }
    if (!Conf.postMediaMaxSize[type]) {
      return {};
    }
    const w = media.w || media.width;
    const h = media.h || media.height;
    if (!w || !h) {
      return Conf.postMediaMaxSize.default;
    }
    const size = Conf.postMediaMaxSize[type];
    if (h > w) {
      return {
        width: Math.floor(size.height / h * w),
        height: size.height,
      }
    }
    else {
      return {
        width: size.width,
        height: Math.floor(size.width / w * h),
      }
    }
  },

  /**
   * 对数组依次处理
   * @param {Array} list
   * @param {Function} action 处理方法，需返回 promise
   * @param {?Int} delay 间隔时间
   */
  roll(list, action, delay = 0) {
    let resolveDone = null;
    let promiseDone = new Promise(resolve => {
      resolveDone = resolve;
    });

    if (!list || !list.length) {
      this.log(`roll error: 空数组`);
      resolveDone({
        errno: 1,
        errmsg: "空数组",
      });
    }
    else {
      let len = list.length;
      let curIndex = 0;
      let errorList = [];
      const roll = () => {
        this.log(`roll ${curIndex + 1}/${len}`);
        action(list[curIndex])
          .then(() => {
            if (++curIndex == len) {
              if (!errorList.length) {
                resolveDone({
                  errno: 0,
                });
                this.log(`roll 已完成`);
              }
              else {
                resolveDone({
                  errno: 1,
                  data: errorList,
                });
                this.log(`roll 已完成, 但是有 ${errorList.length} 条记录报错`, errorList);
              }
              return;
            }
            if (delay > 0) {
              setTimeout(() => {
                roll();
              }, delay);
            }
            else {
              roll();
            }
          })
          .catch(e => {
            let errorItem = {
              curIndex,
              data: list[curIndex],
              error: e,
            };
            this.log(`roll error:`, errorItem);
            errorList.push(errorItem);
            ++curIndex;
            roll();
          });
      }
      roll();
    }

    return promiseDone;
  },

  /**
   * assign
   * @param {Object} obj
   * @param {String} key
   * @param {any} value
   */
  assignKV(obj, key, value) {
    let data = {};
    obj = this.copyObj(obj);
    data[key] = value;
    return Object.assign(obj, data);
  },

  /**
   * Object 转 FormData
   * @param {Object} params
   */
  getForm(params) {
    const fd = new FormData();
    Object.keys(params).forEach(key => {
      if (params[key] instanceof Blob) {
        fd.append(key, params[key], params[key].filename || params[key].name || 'cropimg.png');
      }
      else {
        fd.append(key, params[key]);
      }
    })
    return fd;
  },

  /**
   * 未完成
   */
  showPopHasNotFinished(msg) {
    alert(msg || '未完成');
  },

  /**
   * 颜色插值
   * @param {Color} start
   * @param {Color} end
   * @param {Int} steps
   * @param {?Number} gamma
   * @returns
   */
  getGradientColors(start, end, steps, gamma = 1) {
    let i, j, ms, me, output = [], so = [];
    let normalize = function (channel) {
      return Math.pow(channel / 255, gamma);
    };
    start = this.parseColor(start).map(normalize);
    end = this.parseColor(end).map(normalize);
    for (i = 0; i < steps; i++) {
      ms = i / (steps - 1);
      me = 1 - ms;
      for (j = 0; j < 3; j++) {
        so[j] = this.getFixedPrev0(Math.round(Math.pow(start[j] * me + end[j] * ms, 1 / gamma) * 255).toString(16));
      }
      output.push('#' + so.join(''));
    }
    return output;
  },
  parseColor(hexStr) {
    return hexStr.length === 4
      ? hexStr.substr(1).split('').map((s) => {
        return 0x11 * parseInt(s, 16);
      })
      : [hexStr.substr(1, 2), hexStr.substr(3, 2), hexStr.substr(5, 2)].map((s) => {
        return parseInt(s, 16);
      });
  },

  /**
   * 字符串转日期
   * @param {String} dateStr 日期字符串，仅中国形式，年月日用-/.分隔
   * @return {Date} 日期
   * @example
   *     stringToDate("2013-12-25");
   *     stringToDate("2013-12-25 15:08");
   *     stringToDate("2013-12-25 15:08:01");
   * @deprecated new Date("2013-12-25 15:08:01") 兼容性已经没问题了 20210426
   */
  stringToDate(dateStr) {
    let time = dateStr.split(' ');
    let temp_day = time[0];
    let arys = temp_day.split(/-|\/|\./);
    let _date = new Date(arys[0], arys[1] - 1, arys[2]);

    if (time.length > 1) {
      let temp_second = time[1].split(':');
      _date.setHours.apply(_date, temp_second);
    }

    return _date;
  },

  /**
   * 倒计时
   * @param {Date|String|Number} target 目标日期/目标日期/还差 n 秒
   * @param {?Number} now 当前时间
   * @param {String} formatType 格式化
   * @return {Object}      还差多久
   * @example
   *     countDown(new Date(2014, 5, 1))
   *     // Object {day: 15, hour: 12, minute: 44, second: 32}
   *     countDown(new Date(2014, 4, 1))
   *     // null 已到期
   *    countDown(1)
   *    // Object {day: 0, hour: 0, minute: 0, second: 1}
   */
  countDown(target, now, formatType) {
    let diff;
    if (typeof target == "number") {
      diff = target;
    }
    else {
      now = now || window.G_now || +new Date(),
        target = typeof target == "string"
          ? this.stringToDate(target)
          : target
      target = target.getTime();

      diff = Math.floor(target - now) / 1000;
    }
    if (!diff || diff <= 0) {
      return null;
    }

    let day = Math.floor(diff / 24 / 60 / 60),
      hour = Math.floor((diff - day * 24 * 60 * 60) / 60 / 60),
      minute = Math.floor((diff - day * 24 * 60 * 60 - hour * 60 * 60) / 60),
      second = Math.floor(diff - day * 24 * 60 * 60 - hour * 60 * 60 - minute * 60);

    switch (formatType) {
      case 'hms': {
        return `${this.getFixedPrev0(day * 24 + hour)}:${this.getFixedPrev0(minute)}:${this.getFixedPrev0(second)}`;
      }
      case 'ms': {
        return `${this.getFixedPrev0(minute)}:${this.getFixedPrev0(second)}`;
      }
      case '时分秒': {
        const res = [];
        if(day || hour){
          res.push(`${this.getFixedPrev0(day * 24 + hour)}小时`);
        }
        if(day || hour || minute){
          res.push(`${this.getFixedPrev0(minute)}分`);
        }
        res.push(`${this.getFixedPrev0(second)}秒`);

        return res.join('');
      }
    }
    return {
      day,
      hour,
      minute,
      second,
    };
  },

  /**
   * clone 日期
   * @param {Date} date 原日期
   * @return {Date} clone得到的日期
   */
  cloneDate(date) {
    let time = new Date();
    time.setTime(date.getTime());
    return time;
  },

  /**
   * 下一天
   * @param {Date} date 日期
   * @return {Date} 下一天零点
   */
  getNextDay(date) {
    date = typeof date == 'string' ? this.stringToDate(date) : date;
    date = date || new Date();
    let clone = this.cloneDate(date);
    clone.setDate(clone.getDate() + 1);
    clone.setHours(0, 0, 0, 0);
    return clone;
  },

  /**
   * @param {Node} elem
   * @param {String} className
   */
  addClass(elem, className) {
    if (elem.className === undefined) {
      elem.className = className;
    }
    else if (elem.className !== className) {
      let classes = elem.className.split(/ +/);
      if (classes.indexOf(className) == -1) {
        classes.push(className);
        elem.className = classes.join(' ').replace(/^\s+/, '').replace(/\s+$/, '');
      }
    }
  },

  /**
   * @param {Node} elem
   * @param {String} className
   */
  removeClass(elem, className) {
    if (className) {
      if (elem.className === undefined) {
      }
      else if (elem.className === className) {
        elem.removeAttribute('class');
      }
      else {
        let classes = elem.className.split(/ +/);
        let index = classes.indexOf(className);
        if (index != -1) {
          classes.splice(index, 1);
          elem.className = classes.join(' ');
        }
      }
    } else {
      elem.className = undefined;
    }
  },

  /**
   * @param {Node} elem
   * @param {String} className
   */
  hasClass(elem, className) {
    return new RegExp('(?:^|\\s+)' + className + '(?:\\s+|$)').test(elem.className || '') || false;
  },

  /**
   * @param {Node} elem
   * @param {String} className
   */
  togglerClass(elem, className) {
    if (this.hasClass(elem, className)) {
      this.removeClass(elem, className);
    }
    else {
      this.addClass(elem, className);
    }
  },

  /**
   * 获取 ua
   * @return {String} ua
   */
  initUa() {
    this.ua = this.ua || (window.navigator ? navigator.userAgent.toLowerCase() : "");
    return this.ua;
  },
  /**
   * 是否在 iphone 上
   * @return {Boolean}
   */
  isIphone() {
    this.initUa();
    return /iphone|ipad/i.test(this.ua) && !/android/i.test(this.ua);
  },
  /**
   * 是否在手机上
   * @return {Boolean}
   */
  isPhone() {
    this.initUa();
    if (!this.ua) {
      return true;
    }
    return /android|iphone|360browser|ipad|mobile|blackberry/i.test(this.ua) || typeof window.orientation !== "undefined";
  },
  /**
   * 是否在 android 上
   * @return {Boolean}
   */
  isAndroid() {
    this.initUa();
    return this.isPhone() && !this.isIphone();
  },
  /**
   * 是否在微信
   * @return {Boolean}
   */
  isWeixin() {
    this.initUa();
    return /MicroMessenger/i.test(this.ua);
  },
  /**
   * 是否在 Mac
   * @returns {Boolean}
   */
  isMac() {
    this.initUa();
    return /macintosh|mac os x/i.test(this.ua);
  },
  /**
   * 是否在 QQ 浏览器
   * @return {Boolean}
   */
  isQQ() {
    this.initUa();
    //return /\s+qq\/|mqqbrowser/i.test(this.ua);
    return /QQ\/[0-9]/i.test(this.ua);
  },
  /**
   * 是否在 360 浏览器
   * @return {Boolean}
   */
  is360Browser() {
    this.initUa();
    return (/360browser|360 aphone browser|qhbrowser/i.test(this.ua)) || (window.external && window.external.GetSID);
  },
  // 获取操作系统信息  
  getOsInfo (){  
    var userAgent = navigator.userAgent.toLowerCase();  
    var name = 'Unknown';  
    var version = 'Unknown';  
    if (userAgent.indexOf('win') > -1) {  
      name = 'Windows';  
    if (userAgent.indexOf('windows nt 5.0') > -1) {  
        version = 'Windows 2000';  
    } else if (userAgent.indexOf('windows nt 5.1') > -1 || userAgent.indexOf('windows nt 5.2') > -1) {  
        version = 'Windows XP';  
    } else if (userAgent.indexOf('windows nt 6.0') > -1) {  
        version = 'Windows Vista';  
    } else if (userAgent.indexOf('windows nt 6.1') > -1 || userAgent.indexOf('windows 7') > -1) {  
        version = 'Windows 7';  
    } else if (userAgent.indexOf('windows nt 6.2') > -1 || userAgent.indexOf('windows 8') > -1) {  
        version = 'Windows 8';  
    } else if (userAgent.indexOf('windows nt 6.3') > -1) {  
        version = 'Windows 8.1';  
    } else if (userAgent.indexOf('windows nt 6.2') > -1 || userAgent.indexOf('windows nt 10.0') > -1) {  
        version = 'Windows 10';  
    } else {  
        version = 'Unknown';  
    }  
    } else if (userAgent.indexOf('iphone') > -1) {  
      name = 'iPhone';  
    } else if (userAgent.indexOf('mac') > -1) {  
      name = 'Mac';  
      let temp = navigator.userAgent.match(/(Mac\sOS\sX)\s+([\d\_]+)\)/)
      version = temp[2]
    } else if (userAgent.indexOf('x11') > -1 || userAgent.indexOf('unix') > -1 || userAgent.indexOf('sunname') > -1 || userAgent.indexOf('bsd') > -1) {  
      name = 'Unix';  
    } else if (userAgent.indexOf('linux') > -1) {  
      if (userAgent.indexOf('android') > -1) {  
        name = 'Android';  
    } else {  
        name = 'Linux';  
    }  
    } else {  
      name = 'Unknown';  
    }  
    return { name, version };  
  },
  /**
   * 判断浏览器名称、版本号
   * @return {Object}
   * @example
   */
  getBrowserNameAndVersion(ua) {
    ua = (ua || this.initUa()).toLowerCase();
    const bNameAndVersion = (ua.match(/(msie\s|\s+edge\/|trident.*rv:)([\d.]+)/)) ||
      (ua.match(/(firefox)\/([\d.]+)/)) ||
      (ua.match(/(chrome)\/([\d.]+)/)) ||
      (ua.match(/(opera).([\d.]+)/)) ||
      (ua.match(/(version)\/([\d.]+).*safari/)) ||
      (null);
    if (!bNameAndVersion || !bNameAndVersion.length) {
      return { name: "", version: "" };
    }
    const bName = bNameAndVersion[1];
    return {
      "name": bName == "version" ? "safari" : /trident|msie|edge/.test(bName) ? "ie" : bName,
      "version": bNameAndVersion[2] || ""
    };
  },

  /**
   * 打开新 tab 页或新窗口
   */
  openPage(url, config) {
    if (config) {
      const width = 900;
      const height = 600;

      config = {
        width,
        height,
        top: (window.screen.height - height) / 2,
        left: (window.screen.width - width) / 2,
        ...config,
      }
      if (config.isCenter && (config.width != width || config.height != height)) {
        config = {
          ...config,
          ...{
            top: (window.screen.height - config.height) / 2,
            left: (window.screen.width - config.width) / 2,
          }
        }
      }
      const windowConfig = `height=${config.height},width=${config.width},top=${config.top},left=${config.left},toolbar=no,menubar=no,scrollbars=no,resizable=no,location=no,status=no`;

      // debug
      // this.log("Helper openPage", url, config, windowConfig);

      window.open(url, null, windowConfig);
      return;
    }
    window.open(url);
  },

  /**
   * 补全 url 参数
   * @param {String} url 
   * @example 
   *  repaireUrl("https://item.lenovo.com.cn/product/1022249.html?__cid=wDbjuThe&__sid=735718&__uid=B_GeaSqHJt1&t=1675306578699", {
   *    ch : "ch",
   *  })
   */
  repaireUrl (url, params) {
    if(!url || typeof url != "string"){
      return url;
    }
    
    params = params || this.DeliveryKeys;
    const urlPath = this.getUrlWithOutParams(url);
    const urlParams = this.getParams(url);
    const pageParams = this.getParams();

    Object.keys(params).forEach(key => {
      if(
        pageParams[key] 
        && (!this.ExclusiveKeys[key] || this.ExclusiveKeys[key].every(_ => !urlParams[_]))
      ){
        const _key = params[key];
        urlParams[_key] = urlParams[_key] || pageParams[key];
      }
    })

    if(!Object.keys(urlParams).length){
      return urlPath;
    }

    return `${urlPath}?${this.param(urlParams)}`;
  },
  /**
   * 页面跳转时，参数传递
   */
  DeliveryKeys : {
    src : "src",
    ch : "ch",
    nlpv : "nlpv",      // 浏览器参数
    sid : "sid",        // 浏览器参数
    source : "source",  // 猜你喜欢参数
    debug : "debug",    // 用于调试
    k : "k",  // 用于调试
    user : "user",  // 运营调试
    agent_ver : 'agent_ver',   //企业SaaS参数传递
    agent_uuid : 'agent_uuid', //企业SaaS参数传递
  },
  /**
   * 互斥参数
   * 如: 如果原 url 中有 o_type，不再添加 s_type 参数
   */
  ExclusiveKeys : {
    
  },

  /**
   * 判断 url 是否 gif
   * 1. 以 .gif 结尾
   * 2. 七牛 cdn 上，可能加格式化参数 .gif?
   * @param {String} url
   */
  isGifUrl(url) {
    if (!url || typeof url != "string") {
      return false;
    }
    return /\.gif$/i.test(url) || /\.gif\?/i.test(url);
  },
  /**
   * gif 转静态，只限于七牛
   * @param {String} url
   */
  getStaticGif(url, ext = 'jpg') {
    if (!this.isGifUrl(url)) {
      return '';
    }
    if (/\.gif$/i.test(url)) {
      return `${url}?imageMogr2/format/${ext}`;
    }
    const params = this.getQiniuParams(url);
    params.format = ext;
    return this.getUrlWithOutParams(url) + this.paramQiniu(params);
  },
  // https://qncdn.zhaomi.cn/9c51f5b39c605216779e32902bb87d679d67792b.thumb.gif?imageMogr2/auto-orient/thumbnail/600x600/format/jpg
  qiniuNoValueKey: ["auto-orient"],    // 只有 key，没有 value
  getQiniuParams(url) {
    if (!url || typeof url != "string") {
      return {};
    }
    if (url.indexOf("?") == -1) {
      return {}
    }
    const search = url.split("?")[1];
    if (search.indexOf("imageMogr2") != 0) {
      this.log(`七牛: 无法解析参数`, url);
      return {};
    }
    const params = search.replace("imageMogr2/", "");
    const list = params.split("/");
    const res = {};
    for (let i = 0, len = list.length; i < len; i++) {
      const key = list[i];
      if (this.qiniuNoValueKey.includes(key)) {
        res[key] = true;
      }
      else {
        res[list[i]] = list[i + 1];
        i++;
      }
    }
    return res;
  },
  paramQiniu(params) {
    const data = [];
    Object.keys(params).forEach(key => {
      data.push(key);
      if (!this.qiniuNoValueKey.includes(key)) {
        data.push(params[key]);
      }
    });
    if (!data.length) {
      return '';
    }
    return `?imageMogr2/${data.join('/')}`;
  },
  /**
   * 不支持的格式
   * @param {String} url
   */
  isNotSupportType(url) {
    if (!url) {
      return true;
    }
    return /\.mov$/i.test(url) || /\.mov\?/i.test(url);
  },
  /**
   * 如果是 mov，加载对应的 mp4
   * mov web 无法播放，有可能没有对应的 mp4，但也只是无法播放
   * 七牛上传的 mov 文件，会同时生成 .mov.mp4 文件
   * @param {String} url
   */
  getFixedQiNiuUrl(url) {
    if (!url) {
      return url;
    }
    // mov
    url = url.replace(/\.mov$/i, '.mov.mp4').replace(/\.mov\?/i, '.mov.mp4?');
    return url;
  },
  /**
   * 七牛云
   * 下载文件
   * 见文档 https://developer.qiniu.com/kodo/1659/download-setting
   * @param {String} url
   * @param {String} fileName
   */
  getQiNiuDownloadUrl(url, fileName = '下载') {
    if (!url) {
      return url;
    }
    return `${url}${url.indexOf("?") == -1 ? "?" : "&"}attname=${fileName}`;
  },

  /**
   * 生成固定长宽比的图片，用于占位
   * width/height 取值范围 [1, 100] 正整数
   * @param {Object} param
   */
  getHolderImgUrl({ width = 1, height = 1 } = {}) {
    return `https://p0.ssl.qhimg.com/dc/${width}_${height}_/t0124213d3e61632fd6.png`;
  },
  /**
   * 文件扩展名
   * @param {String} url
   */
  getFileNameExt(url) {
    if (!url || url.indexOf(".") == -1) {
      return '';
    }
    url = this.getUrlWithOutParams(url);
    const piece = url.split(".");
    return piece[piece.length - 1];
  },

  /**
   * 获取当前选择的文字
   */
  getSelected() {
    if (window.getSelection) {
      return window.getSelection().toString();
    }
    else {
      const selection = document.selection?.createRange();
      if (selection.text) {
        return selection.text.toString();
      }
      return '';
    }
  },

  /**
   * 节流
   */
  throttle(fn, delay = 100) {
    let timer = null;
    return (...params) => {
      if(!timer){
        fn(...params);
        timer = setTimeout(() => {
          timer = null;
        }, delay);
      }
    }
  },

  /**
   * 防抖
   */
  debounce(fn, delay = 100){
    let timer = null;
    return (...params) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        fn(...params);
      }, delay);
    }
  },

  /**
   * 日期相关
   */
  dateConfig : {
    hour : Array.from({ length : 24 }).map((_, index) => index),
    minute : Array.from({ length : 60 }).map((_, index) => index),
    second : Array.from({ length : 60 }).map((_, index) => index),
  },

  /**
   * 分享到第三方平台
   * @param {String} platform
   * @param {Object} params
   * @example
   *  shareTo("weibo", {
   *    title : "",
   *    url : "",
   *    pic : "",
   *    desc : "",
   *  })
   */
  shareTo (platform, params = {}) {
    let url = "";
    switch(platform){
      // 微博
      case "weibo":{
        url = `https://service.weibo.com/share/share.php?${this.param({
          url : params.url || '',
          title : params.title || '',
          pic : params.pic || '',
        })}`;
        break;
      }
      // QQ
      case "qq":{
        url = `http://connect.qq.com/widget/shareqq/index.html?${this.param({
          url : params.url || '',
          title : params.title || '',
          pics : params.pic || '',
          summary : params.desc || '',
        })}`;
        break;
      }
      // facebook
      case "facebook":{
        url = `https://www.facebook.com/sharer/sharer.php?${this.param({
          u : params.url,
          title : params.title,
        })}`
        break;
      }
      // qzone
      case "qzone":{
        url = `https://sns.qzone.qq.com/cgi-bin/qzshare/cgi_qzshare_onekey?${this.param({
          url : params.url || '',
          title : params.title || '',
          summary : params.desc || '',
        })}`
        break;
      }
    }

    if(!url){
      return;
    }
    this.openPage(url, true);
  },

  /**
   * 测试环境调试日志
   * @param  {...any} args
   */
  log(...args) {
    if (this.IS_PROD) {
      return;
    }

    console.log(...args, `${this.formatDate(new Date(), 'yyyy-MM-dd hh:mm:ss S')}`);
  },

  /**
   * ARMS 上报
   * name, message 必选
   * @param {Object} params
   * @param {?Object} options
   * @example
   *  report({
   *    name : "",
   *    message : "",
   *  })
   *
   *  report({
   *    name : "",
   *    message : "",
   *  }, {
   *    filename : "",
   *    lineno : 0,
   *    colno : 0,
   *  })
   */
  report (params, options) {
    try{
      if(!params.name || !params.message){
        return;
      }
      window.browserLogger.error(params, options);
    } catch(e){}
  },

  /**
   * 动态加载js
   */
  loadJs (url, cb_succ, cb_fail) {
    const head = document.getElementsByTagName("head")[0];
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.onload = cb_succ || function(){};
    script.onerror = cb_fail || function(){};
    script.src = url;
    head.appendChild(script);
  },

  /**
   * 动态加载js，但是会根据id判断是否已经加载
   * @param {string} url
   * @param {string} id js文件的唯一标识，如果存在则不重新加载
   * @param {function} cb_succ
   * @param {function} cb_fail
   */
  loadJsExist (url, id, cb_succ, cb_fail, useSync) {
    const script = document.querySelector(`#${id}`);
    if (script) {
      cb_succ && cb_succ();
      return;
    }
    const head = document.getElementsByTagName("head")[0];
    const newScript = document.createElement("script");
    newScript.type = "text/javascript";
    if (!useSync) {
      newScript.async = true
    }
    newScript.onload = cb_succ || function(){};
    newScript.onerror = cb_fail || function(){};
    newScript.src = url;
    newScript.id = id;
    head.appendChild(newScript);
  },

  /**
	 * 用户唯一标识
	 */
	midKey: '____guid',
	getMid() {
		this.m2 =
			this.m2 ||
			localStorage.getItem(this.midKey) ||
			this.getCookie(this.midKey) ||
			this.generateMID();
		return this.m2;
	},

  /**
   * 获取 guid，用于广告推荐
   * @returns
   */
  getGuid() {
    return this.getCookie('__guid');
  },

  getHuid() {
    return this.getCookie('__huid');
  },

	_saveUid(mid) {
		// ios 无痕模式可能会报错 QuotaExceededError (DOM Exception 22): The quota has been exceeded
		try {
			localStorage.setItem(this.midKey, mid);
		} catch (e) {
			this.setCookieWithSamesite(this.midKey, mid);
		}

		this.m2 = mid;
	},

  /**
   * base64转file  
   * base64格式：data:image/png;base64,iVBORw0KGgoAAAANSU...
   * @param {*} dataURL base64编码数据
   * @param {*} filename 文件名称
   */
  base64ToFile(base64, filename = '') {
    const arr = base64.split(','),
        mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]);
    let n = bstr.length,
        u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, {type: mime});
  },

	/**
	 * 生成 mid, 并记入 localStorage
	 */
	generateMID() {
		let doc = document,
			nav = navigator,
			screen = window.screen;

		function hash(s) {
			let h = 0,
				g = 0,
				i = s.length - 1;
			for (i; i >= 0; i--) {
				let code = parseInt(s.charCodeAt(i), 10);
				h = ((h << 6) & 0xfffffff) + code + (code << 14);
				if ((g = h & 0xfe00000) != 0) {
					h = h ^ (g >> 21);
				}
			}
			return h;
		}

		function guid() {
			let s = [
				nav.appName,
				nav.version,
				nav.language || nav.browserLanguage,
				nav.platform,
				nav.userAgent,
				screen.width,
				'x',
				screen.height,
				screen.colorDepth,
				doc.referrer,
			].join(''),
				sLen = s.length,
				hLen = window.history.length;

			while (hLen) {
				s += hLen-- ^ sLen++;
			}

			return (Math.round(Math.random() * 2147483647) ^ hash(s)) * 2147483647;
		}

		let id;
		id = [
			hash(doc.domain),
			guid(),
			+new Date() + Math.random() + Math.random(),
		].join('');
		id = id.replace(/\./gi, 'e');
		id = id.substr(0, 32);
		this._saveUid(id);

		return id;
	},

  /**
	 * 种cookie
	 * @param {String} cookieName
	 * @param {String} cookieValue
	 * @param {Number} day         有效时长，单位为日
	 * @param {Object} options     其它属性
	 * @example
	 *     Helper.setCookie("pri", "4", null, { path : "/" });    // 默认365天
	 *     Helper.setCookie("pri", "4", -1, { path : "/" });      // 设置一个过期的cookie，即删除cookie
	 *     option : {
	 *         domain : "xxx",
	 *         path : "xxx",
	 *         secure : "xxx"
	 *     }
	 */
	setCookie(cookieName, cookieValue, day, options) {
		options = options || {};

		let expiredays = parseFloat(day) || 365;
		let exdate = new Date();
		exdate.setTime(exdate.getTime() + expiredays * 86400000);
		let cookieVal =
			cookieName +
			'=' +
			escape(cookieValue) +
			';expires=' +
			exdate.toGMTString();

		cookieVal += options.domain ? ';domain=' + options.domain : '';
		cookieVal += options.path ? ';path=' + options.path : '';
		cookieVal += options.secure ? ';secure=' + options.secure : '';
    if(options.Samesite){
      cookieVal += `;SameSite=${options.Samesite}`;
    }

		document.cookie = cookieVal;
	},
  setCookieWithSamesite (cookieName, cookieValue) {
    this.setCookie(cookieName, cookieValue, null, {
      path : "/",
      domain : this.getCookieDomain(),
      secure : true,
      Samesite : "None",
    });
  },

	/**
	 * 取cookie
	 * @param  {String} cookieName
	 * @return {String} cookieValue
	 */
	getCookie(cookieName) {
		let result = new RegExp(
			'(?:^|; )' + encodeURIComponent(cookieName) + '=([^;]*)',
		).exec(document.cookie);
		if (result) {
			result = result[1];
			try {
				result = decodeURIComponent(result);
			} catch (e) { }
		}
		return result || '';
	},

  // canvas检测
  isSupportWebp () {
    try {
      const img = document.createElement('canvas');
      const imgData = img.toDataURL('image/webp', 0.5);
      return imgData.indexOf('data:image/webp') === 0;
    } catch(err) {
      return false;
    }
  },

  getCookieDomain () {
    const isSou = location.host.includes('sou.com');
    if(isSou){
      return "sou.com";
    }

    const isCom = location.host.includes('360.com');
    if(isCom){
      return "360.com";
    }

    const isNcn = location.host.includes('n.cn');
    if(isNcn){
      return "n.cn";
    }

    return "360.cn";
  },

  getTimeFormatText(date) {
    const minute = 60 * 1000
    const hour = 60 * minute
    const day = 24 * hour
    const month = 31 * day
    const year = 12 * month
    
    if(!date) return ""

    const now = new Date();
    const diff = now.getTime() - date.getTime()

    if(diff > year) {
      return this.formatDate(date, "yyyy年MM月dd日 HH点mm分")
    }
    if(this.formatDate(date, "yyyy年MM月dd日") != this.formatDate(now, "yyyy年MM月dd日")) {
      return this.formatDate(date, "MM月dd日 HH点mm分")
    }
    return this.formatDate(date, "HH点mm分")
  },

  getFixedCooperationModelTip (modelNames) {
    if(!modelNames?.length){
      return "";
    }
    modelNames = modelNames.map(item => {
      if(typeof item == "string"){
        return item;
      }
      return item.name;
    })
    if(modelNames.length == 1){
      return modelNames[0];
    }
    const prevNames = modelNames.slice(0, -1);
    const lastName = modelNames[modelNames.length - 1];
    return `${prevNames.join("、")}和${lastName}的协作`;
  },

  init() {
    console.log(`N Commit: ${this.revision.commit} ${this.revision.time}`);
    this.browserInfo = this.getBrowserNameAndVersion();

    // debug
    if (!this.IS_PROD) {
      window._Conf = Conf;
      window._Helper = this;
    }
  },

  // 三击复制时，trim
  handleTrimCopy (event) {
    const content = window.getSelection?.().toString() || "";
    if(content && event.clipboardData || event.originalEvent){
      let clipboardData = event.clipboardData || window.clipboardData;
      clipboardData.setData("text/plain", content.trim());
      event.preventDefault();
    }
  },

  /**
   * 保存文件
   * @param {String} content 
   * @param {String} fileName 
   */
  saveFile(url, fileName) {
    if(!url || !fileName) {
      return
    }
    const element = document.createElement('a');
    element.setAttribute('href', url);
    element.setAttribute('download', fileName);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  },

  /**
   * 保存文件/跨域
   * @param {String} content 
   * @param {String} fileName 
   */
  saveFileCrossOrigin(url, fileName) {
    if(!url || !fileName) {
      return
    }
    const x = new window.XMLHttpRequest();
    x.open('GET', url, true);
    x.responseType = 'blob';
    x.onload = () => {
        const url = window.URL.createObjectURL(x.response);
        this.saveFile(url, fileName)
    };
    x.send();
  },

  splitMarkdownByImages(markdownText) {
    const pattern = /(!\[.*?\]\(.*?\))/g;
    const parts = markdownText.split(pattern).filter(part => part.trim() !== '');
    return parts;
  },

  async downloadPdfFile(url="", fileName="") {
    if (/^https/.test(url)) {
      let _fileName = fileName;
      // 如果fileName没有文件后缀，则从url里取
      if (!/(\.[a-z]{1,4})(?:$|\?)/gi.test(_fileName) && /(\.[a-z]{1,4})(?:$|\?)/gi.test(url)) {
        _fileName = fileName + RegExp.$1;
      }
      if (/qiniu/.test(url)) {
        const urlObj = new URL(url);
        const params = urlObj.searchParams;
        if (!params.get('attname')) {
          params.set('attname', encodeURIComponent(_fileName))
        }
        this.saveFile(urlObj.href, _fileName)
      }
      else if (/xstore/.test(url)) {
        const urlObj = new URL(url);
        const params = urlObj.searchParams;
        if (!params.get('response-content-disposition')) {
          params.set('response-content-disposition', 'attname');
          params.set('filename', encodeURIComponent(_fileName))
        }
        this.saveFile(urlObj.href, _fileName)
      }
      else {
        if(this.isWeixin()){
          this.saveFile(url, _fileName);
        }else{
          const blob = await fetch(url).then(res => res.blob()).catch(err => null);
          if (blob) {
            const url = URL.createObjectURL(blob);
            this.saveFile(url, _fileName);
            URL.revokeObjectURL(url);
          }
        }
        
      }
    }
  },
  /**
    * 下载文件
    * @typedef {Object} file
    * @property {string} url - 文件url
    * @property {string} title - 文件名称
   */
  async downloadFile (file)  {
    const {url, title:fileName} = file
    if (/^https/.test(url)) {
      let _fileName = fileName;
      // 如果fileName没有文件后缀，则从url里取
      if (!/(\.[a-z]{1,4})(?:$|\?)/gi.test(_fileName) && /(\.[a-z]{1,4})(?:$|\?)/gi.test(url)) {
        _fileName = fileName + RegExp.$1;
      }
      if (/qiniu/.test(url)) {
        const urlObj = new URL(url);
        const params = urlObj.searchParams;
        if (!params.get('attname')) {
          params.set('attname', encodeURIComponent(_fileName))
        }
        this.saveFile(urlObj.href, _fileName)
      }
      else if (/xstore/.test(url)) {
        const urlObj = new URL(url);
        const params = urlObj.searchParams;
        if (!params.get('response-content-disposition')) {
          params.set('response-content-disposition', 'attname');
          params.set('filename', encodeURIComponent(_fileName))
        }
        this.saveFile(urlObj.href, _fileName)
      }
       else {
        if(this.isWeixin()){
          this.saveFile(url, _fileName);
        }else{
          const blob = await fetch(url).then(res => res.blob()).catch(err => null);
          if (blob) {
            const url = URL.createObjectURL(blob);
            this.saveFile(url, _fileName);
          }
        }
      }
    }
  },
}

Core.init();

export default Core;